/**
 * Ressourcen-Repository: Notizen, Lesezeichen und ihre Dateianhaenge.
 */
import { db } from '@/db/schema';
import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import { nowInstant } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import { isFileTooLarge, MAX_FILE_BYTES } from '@/domain/resources/attachments';
import { bytesToBlob } from '@/lib/backup/attachments';
import { parseTags } from '@/domain/resources/search';
import type {
  Attachment, ID, ParaBucket, Resource, ResourceKind,
} from '@/domain/types';

export type ResourceDraft = Pick<Resource, 'title'>
  & Partial<Pick<Resource, 'kind' | 'body' | 'url' | 'tags' | 'bucket' | 'areaId' | 'read' | 'subjectId' | 'schulArt'>>;

export const PARA_BUCKETS: ParaBucket[] = ['inbox', 'projekt', 'bereich', 'ressource', 'archiv'];

export class AttachmentTooLargeError extends Error {
  constructor(readonly size: number) {
    super(`Datei ist ${size} Bytes gross, erlaubt sind ${MAX_FILE_BYTES}.`);
    this.name = 'AttachmentTooLargeError';
  }
}

export async function listResources(): Promise<Resource[]> {
  return db.resources.toArray();
}

export async function getResource(id: ID): Promise<Resource | undefined> {
  return db.resources.get(id);
}

export async function createResource(draft: ResourceDraft): Promise<Resource> {
  const ts = nowInstant();
  const resource: Resource = {
    id: newId(),
    areaId: draft.areaId ?? null,
    kind: draft.kind ?? 'notiz',
    title: draft.title.trim(),
    body: draft.body?.trim() || undefined,
    url: draft.url?.trim() || undefined,
    tags: draft.tags ?? [],
    bucket: draft.bucket ?? 'inbox',
    read: draft.read,
    pinned: 0,
    ...(draft.subjectId ? { subjectId: draft.subjectId } : {}),
    ...(draft.schulArt ? { schulArt: draft.schulArt } : {}),
    createdAt: ts,
    updatedAt: ts,
  };
  await db.resources.add(resource);
  return resource;
}

/**
 * Schnellerfassung fuer die Ideen-Inbox.
 *
 * Genau ein Eingabefeld, sonst nichts. Wer beim Aufschreiben erst einen
 * Bereich und ein Fach waehlen muss, schreibt die spontane Idee nicht auf -
 * deshalb landet alles im Eingangsfach und wird SPAETER einsortiert.
 *
 * Eine eingefuegte URL wird als Lesezeichen erkannt: der haeufigste Fall von
 * "das will ich mir merken" ist ein Link, und ihn danach von Hand umzustellen
 * waere genau die Reibung, die hier vermieden werden soll.
 *
 * `areaId` kommt vom Aufrufer aus dem aktiven Bereichsfilter. Ohne ihn waere
 * eine Idee, die man bei gefiltertem Bereich erfasst, gespeichert und sofort
 * ausgeblendet (Roadmap T-01).
 */
export async function quickCapture(input: string, areaId: ID | null = null): Promise<Resource> {
  const text = input.trim();
  const url = alsUrl(text);

  if (url) {
    return createResource({ kind: 'link', title: lesbarerName(url), url: text, areaId });
  }

  // Erste Zeile wird der Titel, der Rest der Text - so darf man drauflosschreiben.
  const [erste = '', ...rest] = text.split('\n');
  return createResource({
    title: erste.trim() || 'Ohne Titel',
    body: rest.join('\n').trim() || undefined,
    areaId,
  });
}

/** Eine einzelne http(s)-Adresse, sonst `null`. */
export function alsUrl(text: string): URL | null {
  if (/\s/.test(text)) return null;
  try {
    const url = new URL(text);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/** `'example.org/artikel'` statt der vollen URL - als Titelvorschlag. */
export function lesbarerName(url: URL): string {
  const pfad = url.pathname.replace(/\/$/, '');
  return `${url.hostname.replace(/^www\./, '')}${pfad}`;
}

export async function updateResource(
  id: ID,
  patch: Partial<Omit<Resource, 'id' | 'createdAt'>>,
): Promise<void> {
  await db.resources.update(id, { ...patch, updatedAt: nowInstant() });
}

/** Schlagworte aus freier Eingabe setzen. */
export async function setTags(id: ID, input: string): Promise<void> {
  await updateResource(id, { tags: parseTags(input) });
}

/**
 * Aus dem Eingangsfach einsortieren: Fach und Bereich in einem Schritt.
 * Genau die Bewegung, fuer die die Inbox existiert.
 */
export async function fileResource(
  id: ID,
  bucket: ParaBucket,
  areaId: ID | null,
): Promise<void> {
  await updateResource(id, { bucket, areaId });
}

export async function togglePinned(id: ID): Promise<void> {
  const r = await db.resources.get(id);
  if (!r) return;
  await updateResource(id, { pinned: r.pinned ? 0 : 1 });
}

export async function toggleRead(id: ID): Promise<void> {
  const r = await db.resources.get(id);
  if (!r) return;
  await updateResource(id, { read: !r.read });
}

/**
 * Loescht die Ressource samt ihrer Anhaenge - sonst blieben verwaiste Blobs.
 * 30 Tage im Papierkorb (F-45), die Bytes der Anhaenge nur auf diesem Geraet.
 */
export async function deleteResource(id: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'resources', zeileIds: [id] }, () => db.transaction('rw', db.resources, db.attachments, async () => {
    await db.attachments.where('resourceId').equals(id).delete();
    await db.resources.delete(id);
  }));
}

export async function changeKind(id: ID, kind: ResourceKind): Promise<void> {
  // Aus einem Lesezeichen eine Notiz zu machen heisst, die URL loszuwerden.
  await updateResource(id, kind === 'notiz' ? { kind, url: undefined } : { kind });
}

// ---------------------------------------------------------------------------
// Anhaenge
// ---------------------------------------------------------------------------

export async function addAttachment(resourceId: ID, file: File): Promise<Attachment> {
  if (isFileTooLarge(file.size)) throw new AttachmentTooLargeError(file.size);

  // Rohe Bytes statt des File-Objekts: ein Blob ueberlebt `structuredClone`
  // nicht ueberall, siehe Kommentar an `Attachment` in domain/types.ts.
  const bytes = new Uint8Array(await file.arrayBuffer());

  const attachment: Attachment = {
    id: newId(),
    resourceId,
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: bytes.length,
    bytes,
    createdAt: nowInstant(),
  };

  await db.attachments.add(attachment);
  // Die Ressource gilt damit als geaendert - sonst rutscht sie in der
  // Sortierung nach hinten, obwohl gerade etwas dazugekommen ist.
  await db.resources.update(resourceId, { updatedAt: nowInstant() });
  return attachment;
}

export async function deleteAttachment(id: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'attachments', zeileIds: [id] }, () => db.attachments.delete(id));
}

export async function attachmentsFor(resourceId: ID): Promise<Attachment[]> {
  return db.attachments.where('resourceId').equals(resourceId).sortBy('createdAt');
}

/** Fuer Download und Vorschau - erst hier entsteht ueberhaupt ein Blob. */
export function attachmentBlob(attachment: Attachment): Blob {
  return bytesToBlob(attachment.bytes, attachment.mimeType);
}

/**
 * Summe aller Anhaenge in Bytes.
 *
 * Liest bewusst nur das `size`-Feld statt der Blobs - sonst zoege eine blosse
 * Anzeige die gesamte Dateisammlung in den Speicher.
 */
export async function totalAttachmentBytes(): Promise<number> {
  let summe = 0;
  await db.attachments.each((a) => { summe += a.size; });
  return summe;
}
